import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  getRequiredObjectId,
  selectSiteAssignmentAndTransform,
  SiteAssignmentBuilder,
  SiteAssignmentCompanyAssocEntity,
  SiteAssignmentEntity,
  SiteAssignmentType,
  TransportCategory,
  useCreateSiteAssignmentMutation,
  useGetSiteAssignmentQuery,
  useUpdateSiteAssignmentMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {notifications} from "@mantine/notifications";
import {v4 as uuid} from 'uuid';
import {
  CompanySiteAssignmentFormValues
} from "../../components/form/site-assignment/CompanySiteAssignmentForm/form-context";
import {
  CompanySiteAssignmentForm,
  CompanySiteAssignmentFormOptions
} from "../../components/form/site-assignment/CompanySiteAssignmentForm/CompanySiteAssignmentForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {Result} from "../../components/result/Result";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

type Props = {
  id?: string;
  siteId: string;
  type: SiteAssignmentType;
  category: TransportCategory;
  copy?: boolean;
  options?: CompanySiteAssignmentFormOptions
  formDefaults?: Partial<CompanySiteAssignmentFormValues>
}

export const CompanySiteAssignmentFormPageRoute = () => {
  const {state} = useLocation();
  const navigate = useNavigate()

  const id = state?.id;
  const siteId = state?.siteId
  const type = state?.type
  const category = state?.category
  const copy = state?.copy;
  const options = state?.options
  const formDefaults = state?.formDefaults;

  if (!siteId || !type || !category) {
    navigate(-1);
    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Result title={"Error"} message={"Site, Type, or Category Undefined."}/>
          </Container>
        )}
      </ThemedPage>
    );
  }

  return (
    <CompanySiteAssignmentFormPage
      id={id}
      copy={copy}
      siteId={siteId}
      type={type}
      category={category}
      options={options}
      formDefaults={formDefaults}/>
  );
};


export const toFormDefaults = (assignment: SiteAssignmentEntity | undefined): Partial<CompanySiteAssignmentFormValues> => {
  return {
    strategy: assignment?.strategy,
    assocs: assignment?.companyAssocs?.map((a) => {
      return {
        company: a.companyId,
        normalRank: a.normalPreferenceRank,
        serviceRank: a.servicePreferenceRank,
      };
    })
  };
};

export const CompanySiteAssignmentFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    siteId,
    type,
    category,
    options,
    formDefaults
  } = props;

  const navigate = useNavigate();
  const {regionId} = useApp()
  const {t} = useTranslation(["translation"]);

  const {currSiteAssignment, isLoading} = useGetSiteAssignmentQuery(id ?? skipToken, {
    selectFromResult: ({data, isLoading}) => ({
      currSiteAssignment: data ? selectSiteAssignmentAndTransform(data) : undefined,
      isLoading: isLoading
    })
  });

  const _formDefaults: Partial<CompanySiteAssignmentFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaults(currSiteAssignment);
  }, [currSiteAssignment, formDefaults]);

  const title: string = useMemo(() => {
    if (currSiteAssignment && !copy) {
      return t("common.entity.siteassignment.copmanyassoc.title.edit", "Edit Company - Site Assignment")
    } else {
      return t("common.entity.siteassignment.copmanyassoc.title.new", "New Company - Site Assignment")
    }
  }, [currSiteAssignment, copy, t]);

  const [createSiteAssignment, {isLoading: isCreating}] = useCreateSiteAssignmentMutation();
  const [updateSiteAssignment, {isLoading: isUpdating}] = useUpdateSiteAssignmentMutation();

  const onSiteAssignmentSubmit = useCallback((formValues: CompanySiteAssignmentFormValues) => {
    if (!regionId) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Region Unknown"
      })
      return
    }

    if (currSiteAssignment && !copy) {
      const companyAssocs = formValues.assocs.map((c) => {
        const existingAssoc = currSiteAssignment.companyAssocs?.find(
          (a) => a.companyId === getRequiredObjectId(c.company)
        );
        if (existingAssoc) {
          existingAssoc.normalPreferenceRank = c.normalRank;
          existingAssoc.servicePreferenceRank = c.serviceRank;
          return existingAssoc;
        } else {
          return SiteAssignmentCompanyAssocEntity.create(
            currSiteAssignment.id,
            getRequiredObjectId(c.company),
            c.normalRank,
            c.serviceRank
          );
        }
      });

      const assignment = currSiteAssignment.clone();
      assignment.strategy = formValues.strategy;
      assignment.companyAssocs = companyAssocs;
      assignment.lastModifiedDttm = adjustToTimeZone(new Date()).getTime();

      updateSiteAssignment(assignment)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Site Assignment Updated",
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    } else {
      const newId = uuid()

      const companyAssocs = formValues.assocs.map((s) => {
        return SiteAssignmentCompanyAssocEntity.create(
          newId,
          getRequiredObjectId(s.company),
          s.normalRank,
          s.serviceRank
        );
      });

      const assignment = SiteAssignmentBuilder.builder(newId)
        .setStrategy(formValues.strategy)
        .setType(type)
        .setCategory(category)
        .setSite(siteId)
        .setRegion(regionId)
        .setCompanyAssocs(companyAssocs)
        .build();

      createSiteAssignment(assignment)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Site Assignment Created",
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [regionId, currSiteAssignment, copy, updateSiteAssignment, navigate, t, type, category, siteId, createSiteAssignment]);

  if (!regionId) {
    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Result title={"Error"} message={"Region Unknown."}/>
          </Container>
        )}
      </ThemedPage>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={1000} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <CompanySiteAssignmentForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onSiteAssignmentSubmit}
                  options={{
                    ...options,
                    regionId: options?.regionId ?? currSiteAssignment?.regionId ?? regionId,
                  }}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

